import { lazy } from 'react'

/**
 * Módulo: Plataforma
 * Administración de plataforma: roles, consultores, IA, APIs, biblioteca, protocolos.
 * Solo visible para admin_plataforma.
 */
export default {
  id: 'plataforma',
  nombre: 'Plataforma',
  descripcion: 'Administración de la plataforma: roles, consultores, IA, biblioteca',
  icono: 'settings',
  tipo: 'admin',
  version: '1.0.0',
  // Solo super admin

  // Cada ruta declara el código de permiso que exige (`permiso`): `App.jsx` no
  // la monta si el rol no tiene `ver` sobre ese código, con la misma regla que
  // el sidebar (`@core/lib/permisoModulo`). Es el código del ítem de menú que
  // lleva a la ruta; los hijos de `ia` heredan el del padre. Lo fija
  // `src/core/lib/menuRutaPermiso.guard.test.js`.
  routes: [
    { path: 'catalogo', permiso: 'configuracion', component: lazy(() => import('./pages/CatalogoAdminPage')) },
    { path: 'modulos-plataforma', permiso: 'modulos-plataforma', component: lazy(() => import('./pages/ModulosAdminPage')) },
    { path: 'planes', permiso: 'planes', component: lazy(() => import('./pages/PlanesAdminPage')) },
    { path: 'observabilidad', permiso: 'observabilidad', component: lazy(() => import('./pages/ObservabilidadPage')) },
    // v2 (#49 B9.2) — ConsultoresPage retirada. UsuariosPage (módulo gestión, /usuarios) es la única página de ABM de usuarios.
    { path: 'roles', permiso: 'roles', component: lazy(() => import('./pages/RolesPage')) },
    // Permisos: matriz editable (filas=roles × columnas=módulos.acciones). Dual access:
    //   /permisos            → matriz completa
    //   /permisos?rolId=X    → misma matriz preseleccionada en ese rol (entrada desde RolesPage)
    { path: 'permisos', permiso: 'roles', component: lazy(() => import('./pages/PermisosMatrizPage')) },
    // Auditoría del modelo de permisos (B8) — log inmutable de cambios.
    { path: 'auditoria-permisos', permiso: 'auditoria-permisos', component: lazy(() => import('./pages/AuditoriaPermisosPage')) },
    // #87 — Clasificar items del sidebar por eje (operativo/plataforma).
    { path: 'items-sidebar', permiso: 'modulos-plataforma', component: lazy(() => import('./pages/ItemsSidebarPage')) },
    // "Vista detallada de empresa" — habilitación por rol de tabs/dashboards
    // de la página de empresa (sistema de vistas, core/lib/vistasEmpresa).
    { path: 'vistas-empresa', permiso: 'modulos-plataforma', component: lazy(() => import('./pages/VistasEmpresaPage')) },
    // Organigrama — árbol de relaciones reportaA entre usuarios.
    { path: 'organigrama', permiso: 'organigrama', component: lazy(() => import('./pages/OrganigramaPage')) },
    // Plan IA Paso 7 — /ia es una sección con sub-rutas. IALayout es el shell
    // (header + sub-nav + <Outlet/>); cada hijo es una sub-sección con su URL.
    {
      path: 'ia',
      permiso: 'ia-config',
      component: lazy(() => import('./pages/IALayout')),
      children: [
        { index: true, component: lazy(() => import('./pages/AIPanelPage')) },
        { path: 'salud', component: lazy(() => import('./pages/AISaludPage')) },
        { path: 'prompts', component: lazy(() => import('./pages/AIConfigPage')) },
        { path: 'credenciales', component: lazy(() => import('./pages/AIConfigPage')) },
        { path: 'pricing', component: lazy(() => import('./pages/AIConfigPage')) },
        { path: 'seeds', component: lazy(() => import('./pages/AISeedsPage')) },
        // Reuniones — ABM de tipos de reunión + prompts del análisis IA.
        { path: 'reuniones', component: lazy(() => import('./pages/AutoMeetingPage')) },
        { path: 'reuniones/:tipoId', component: lazy(() => import('./pages/AutoMeetingPage')) },
        // Motor — jobs del motor de análisis IA (antes /prompts-ia).
        { path: 'motor', component: lazy(() => import('./pages/PromptsIAPage')) },
        // Plan IA Paso 9.4 — Aprendizaje: cola de calificación + curación.
        { path: 'aprendizaje', component: lazy(() => import('./pages/AIAprendizajePage')) },
      ],
    },
    // Plan IA Paso 7.7 — /auto-meeting se consolidó en /ia/reuniones.
    // Estas rutas quedan como redirect para no romper links viejos.
    { path: 'auto-meeting',          permiso: 'ia-config', component: lazy(() => import('./pages/AutoMeetingRedirect')) },
    { path: 'auto-meeting/:tipoId',  permiso: 'ia-config', component: lazy(() => import('./pages/AutoMeetingRedirect')) },
    { path: 'api-terceros', permiso: 'api-terceros', component: lazy(() => import('./pages/ApiTercerosPage')) },
    { path: 'biblioteca', permiso: 'biblioteca', component: lazy(() => import('./pages/BibliotecaPage')) },
    { path: 'protocolos', permiso: 'protocolos', component: lazy(() => import('./pages/ProtocolosPage')) },
    { path: 'agentes', permiso: 'agentes', component: lazy(() => import('./pages/AgentesPage')) },
    // Fase 2e Compliance — vista global de eventos críticos auditados
    { path: 'audit-logs', permiso: 'audit-logs', component: lazy(() => import('./pages/AuditLogsAdminPage')) },
    // Instalaciones self-service — quién intentó instalar la app desde el
    // catálogo del canal, terminen o no. El ÍTEM del sidebar vive en
    // `plataforma-operacion/config.js` (mismo patrón que Observabilidad y
    // Audit logs); acá vive la ruta.
    { path: 'instalaciones', permiso: 'instalaciones', component: lazy(() => import('./pages/InstalacionesSelfServicePage')) },
    // Debugging — SE MUDÓ a su propio módulo el 7-ago-2026 (ADR 064 tramo 5).
    // Las dos rutas y la página viven ahora en `src/modules/debugging/`, que
    // hasta entonces era una carpeta con `lib/` y nada más. No re-agregarlas acá.
  ],

  // Sidebar organizado en sub-secciones con dividers y subheaders.
  // El renderer (Layout.jsx > MenuSection) interpreta:
  //   { type: 'divider' }                 → línea separadora
  //   { type: 'subheader', label: 'X' }   → subtítulo dentro del grupo
  //
  // #87 — Cada item declara `permisoModulo: 'X'` (código de módulo del
  // catálogo). El sidebar muestra el item solo si el rol del usuario tiene
  // 'ver' sobre ese módulo. Wizard ve todo.
  // Split del 7-jul (Mariano): la sección "Plataforma" se repartió en tres
  // grupos nuevos del sidebar —
  //   • PLATAFORMA OPERACIÓN     → Observabilidad, 🐛 Debugging, Audit logs
  //                                (+ Difusiones desde comunicaciones)
  //   • USUARIOS Y PERMISOS      → Usuarios, Organigrama, Roles, Permisos, Auditoría
  //   • PLATAFORMA CONFIGURACIÓN → SLAs, Accionables, Frameworks, Dimensiones,
  //                                Motor criticidad, Reportes, Catálogo, Módulos,
  //                                Planes, IA, APIs, Agentes, Biblioteca, Protocolos
  // "Items del sidebar" se descartó (no se usa).
  //
  // Este módulo queda SIN ítems de menú → el grupo "Plataforma" ya no aparece
  // en el sidebar. Se mantiene REGISTRADO igual porque acá viven todas las
  // `routes` (abajo) que consumen los ítems mudados. NO borrar el módulo.
  menuItems: [],

  features: [
    'catalogo-plataforma',
    'roles',
    'ai-config',
    'api-terceros',
    'biblioteca',
    'protocolos',
    'agentes',
    'audit-logs',
  ],

  // permisoModulos (#58): se deriva del rol — eje Plataforma.
  // Incluye el código de cada ítem del menú cuya ruta monta este módulo: lo exige `src/core/lib/menuRutaPermiso.guard.test.js`.
  permisoModulos: [
    'usuarios', 'roles', 'configuracion', 'instalaciones',
    'observabilidad', 'audit-logs', 'organigrama', 'auditoria-permisos', 'agentes', 'modulos-plataforma',
    'planes', 'ia-config', 'api-terceros', 'biblioteca', 'protocolos',
  ],

}
